//* 장례식장 검색용 모달
import React, { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { useFuneralHomeInfoListHook } from '@shared/api/funeralHomeInfo/funeralHomeInfo.hook';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { FuneralHomeInfoListStateSelector } from '@shared/api/funeralHomeInfo/funeralHomeInfo.selector';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { CustomCallback } from '@shared/types';
import FuzzySearch from 'fuzzy-search';
import { SubmitHandler } from 'react-hook-form';

import FuneralHomeInfoSearchDetail from './FuneralHomeInfoSearchDetail';
import FuneralHomeInfoSearchForm, {
  IFuneralHomeInfoForm,
} from './FuneralHomeInfoSearchForm';
import FuneralHomeInfoSearchList from './FuneralHomeInfoSearchList';

export enum FuneralHomeInfoModalStepEnum {
  List = 'list',
  Detail = 'detail',
  Form = 'form',
}

interface Props {
  openId: string;
  onValueChange: CustomCallback<string, void>;
  setFuneralHomeInfoName: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * @param onValueChange: 장례식장 선택시 실제 입력 하는 곳에서 변경되도록 받는 callBack function
 * @returns
 */

const FuneralHomeInfoSearchModal: FC<Props> = function FuneralHomeInfoSearchModal({
  openId,
  onValueChange,
  setFuneralHomeInfoName,
}: Props) {
  //장례식장 전체 들고오기
  useFuneralHomeInfoListHook();
  const funeralHomeInfoListState = useRecoilValue(FuneralHomeInfoListStateSelector);

  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));

  const [step, setStep] = useState<FuneralHomeInfoModalStepEnum>(
    FuneralHomeInfoModalStepEnum.List,
  );

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [selectedFuneralHome, setSelectedFuneralHome] = useState<FuneralHomeInfo<
    undefined,
    string
  > | null>(null);

  // before button render
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!open) timeout = setTimeout(() => setSelectedPage(1), 200);
    return () => clearTimeout(timeout);
  }, [open]);

  const searchedFuneralHomeInfoList = useMemo(() => {
    if (funeralHomeInfoListState.status === 'success' && funeralHomeInfoListState.data) {
      const funeralHomeInfoList = funeralHomeInfoListState.data;
      const searcher = new FuzzySearch(funeralHomeInfoList, ['name', 'address']);

      return searcher.search(searchValue);
    }
    return [] as FuneralHomeInfo<undefined, string>[];
  }, [funeralHomeInfoListState, searchValue]);

  const goToListStep = () => setStep(FuneralHomeInfoModalStepEnum.List);
  const goToDetailStep = () => setStep(FuneralHomeInfoModalStepEnum.Detail);
  const goToFormStep = () => setStep(FuneralHomeInfoModalStepEnum.Form);

  const renderContents = useMemo(() => {
    const onItemClickInListStep = (item: FuneralHomeInfo<undefined, string>) => {
      setSelectedFuneralHome(item);
      goToDetailStep();
    };

    const onConfirmInDetailStep = () => {
      if (selectedFuneralHome) {
        setFuneralHomeInfoName(selectedFuneralHome.name);
        onValueChange(selectedFuneralHome._id);
        setOpen(false);
        goToListStep();
      }
    };

    const onCancleInDetailStep = () => {
      setSelectedFuneralHome(null);
      goToListStep();
    };

    const onSubmitInFormStep: SubmitHandler<IFuneralHomeInfoForm> = (data) => {
      // 1. funeralHomeInfo _id를 가져와야함
      // 2. form으로 받은 내용으로 FuneralHomeInfoRequest 내용 create
      // 3. funeralHomeInfoRequest 생성
      // 4. 최종 form에 안착
      data.phoneNumber = data.phoneNumber.replaceAll('-', '');
      console.log(data);
    };

    switch (step) {
      case FuneralHomeInfoModalStepEnum.List:
        return (
          <FuneralHomeInfoSearchList
            searchedFuneralHomeInfoList={searchedFuneralHomeInfoList}
            onItemClick={onItemClickInListStep}
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
          />
        );
      case FuneralHomeInfoModalStepEnum.Detail:
        return (
          <>
            {selectedFuneralHome && (
              <FuneralHomeInfoSearchDetail
                selectedFuneralHome={selectedFuneralHome}
                onConfirm={onConfirmInDetailStep}
                onCancle={onCancleInDetailStep}
                goToFormStep={goToFormStep}
              />
            )}
          </>
        );
      case FuneralHomeInfoModalStepEnum.Form:
        return <FuneralHomeInfoSearchForm onSubmit={onSubmitInFormStep} />;
      default:
        return;
    }
  }, [
    onValueChange,
    searchedFuneralHomeInfoList,
    selectedFuneralHome,
    selectedPage,
    setFuneralHomeInfoName,
    setOpen,
    step,
  ]);

  return (
    <Modal openId={openId} className="w-full space-y-4" position="start">
      <h3 className="bugo-h1">
        {step === FuneralHomeInfoModalStepEnum.Form
          ? '장례식장 직접입력'
          : '장례식장 검색'}
      </h3>
      {step !== FuneralHomeInfoModalStepEnum.Form && (
        <TextField
          className="w-full"
          placeholder="장례식장 이름 및 지역을 검색하세요."
          onChange={(e) => {
            setSelectedPage(1);
            setSearchValue(e.target.value);
            goToListStep();
          }}
        />
      )}
      {renderContents}
    </Modal>
  );
};

export default FuneralHomeInfoSearchModal;
